import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CloudCog, Droplets, FlaskConical, Ruler, Wind } from "lucide-react";
import { Reveal, SectionTag } from "../components/bits";
import SEO from "../components/SEO";
import { ARTICLES } from "../data/guides";

const ICONS = { BookOpen, CloudCog, Droplets, FlaskConical, Ruler, Wind };

function GuideCard({ a, i }) {
  const Icon = ICONS[a.icon] || BookOpen;
  return (
    <Reveal delay={i * 0.06}>
      <Link
        to={`/guides/${a.slug}`}
        data-testid={`guide-card-${a.slug}`}
        className="spotlight-card group flex h-full flex-col rounded-2xl border border-[#39FF14]/15 bg-[#0A150F] p-7"
      >
        <div className="flex items-start justify-between">
          <Icon className="h-6 w-6 text-[#39FF14]" />
          <span className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]/70">{a.kind}</span>
        </div>
        <h3 className="font-display mt-5 text-xl font-bold text-[#F0FDF4] transition-colors group-hover:text-[#39FF14]">{a.title}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#9FB8A8]">{a.excerpt}</p>
        <span className="mt-5 inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
          Read the guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    </Reveal>
  );
}

export default function Guides() {
  const tech = ARTICLES.filter((a) => a.kind === "Tech knowledge");
  const tips = ARTICLES.filter((a) => a.kind === "Expert tip");
  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-28 sm:px-8" data-testid="guides-page">
      <SEO
        title="Grow Guides & Tech Knowledge — LED Grow Light Tips | SuperTechLED"
        description="Expert LED grow light advice from 20+ years growing indoors: hanging distance charts, true watts explained, grow room ventilation, and sealed rooms with CO2."
      />
      <Reveal>
        <SectionTag>Grow guides</SectionTag>
        <h1 className="font-display mt-4 max-w-3xl text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Field notes from <span className="text-[#39FF14]">20+ years</span> growing indoors
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
          Practical, no-nonsense knowledge from our in-house grower — two decades in hydroponics, indoor gardening
          and plant problem diagnosis. Every guide gets its own page, so bookmark the ones you need.
        </p>
      </Reveal>

      <Reveal className="mt-16">
        <h2 className="font-display text-2xl font-bold text-[#F0FDF4] sm:text-3xl">Tech knowledge</h2>
      </Reveal>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {tech.map((a, i) => (
          <GuideCard key={a.slug} a={a} i={i} />
        ))}
      </div>

      <Reveal className="mt-16">
        <h2 className="font-display text-2xl font-bold text-[#F0FDF4] sm:text-3xl">Expert tips</h2>
      </Reveal>
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {tips.map((a, i) => (
          <GuideCard key={a.slug} a={a} i={i} />
        ))}
      </div>

      <Reveal className="mt-24">
        <div className="glass rounded-3xl p-8 sm:p-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <img src="/img/brand/alien.png" alt="SuperTechLED alien mascot" className="h-20 w-auto shrink-0 drop-shadow-[0_0_24px_rgba(57,255,20,0.3)]" />
              <div>
                <h3 className="font-display text-xl font-bold text-[#F0FDF4]">Written by our in-house grower</h3>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#9FB8A8]">
                  Over 20 years in hydroponics and indoor gardening — equipment, technique and plant problem
                  diagnosis. Wrestling with something in your tent? Ask us — we're growers first, retailers second.
                </p>
              </div>
            </div>
            <Link
              to="/shop"
              data-testid="guides-shop-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-7 py-3.5 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
            >
              Shop the range <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
